import React, { useState } from 'react';
import { Button, Chip } from '@lumx/react';
import { NodeType, OUNode, Site } from '../../types';
import { Icon } from './icons';
import ParentPicker from './ParentPicker';

interface Props {
  nodes: OUNode[];
  sites: Site[];
  onSelect: (nodeId: string) => void;
  onCreate?: (name: string, type: NodeType, parentId: string | null) => void;
  onUnassign?: () => void;
}

const OUPickerDropdown: React.FC<Props> = ({ nodes, sites, onSelect, onCreate, onUnassign }) => {
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<NodeType>('OrganizationalUnit');
  const [newParent, setNewParent] = useState<string | null>(null);

  const filtered = nodes.filter(n =>
    n.id !== '__global__' &&
    n.type === 'OrganizationalUnit' &&
    n.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = () => {
    if (!newName.trim() || !onCreate) return;
    onCreate(newName.trim(), newType, newParent);
    setNewName('');
    setNewType('Location');
    setNewParent(null);
    setShowCreate(false);
  };

  return (
    <div className="picker-dropdown" style={{ minWidth: 260 }}>
      {onUnassign && (
        <>
          <button className="picker-unassign-btn" onClick={onUnassign}>
            Unassign
          </button>
          <div className="picker-divider" />
        </>
      )}
      <div className="picker-search">
        {Icon.search}
        <input
          autoFocus
          placeholder="Search Org units…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div className="picker-list">
        <div className="picker-option" onClick={() => onSelect('__global__')}>
          <span className="name-icon">{Icon.globe}</span>
          <span>Global / Platform</span>
        </div>
        {filtered.map(n => {
          const parent = n.parentId ? nodes.find(p => p.id === n.parentId) : null;
          return (
            <div key={n.id} className="picker-option" onClick={() => onSelect(n.id)}>
              <span className="name-icon">{n.type === 'Location' ? Icon.location : Icon.building}</span>
              <span style={{ flex: 1 }}>{n.name}</span>
              {parent && (
                <span style={{ fontSize: 10, color: 'var(--color-dark-l3)' }}>{parent.name}</span>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && search && (
          <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--color-dark-l3)' }}>
            No results for "{search}"
          </div>
        )}
      </div>
      {onCreate && (
        <>
          <div className="picker-divider" />
          {!showCreate ? (
            <div className="picker-create" onClick={() => { setShowCreate(true); setNewName(search); }}>
              {Icon.plus}
              Create new unit{search ? `: "${search}"` : ''}
            </div>
          ) : (
            <div className="create-ou-form">
              <div className="create-ou-form-title">New unit</div>
              <div className="form-field">
                <label className="form-label">Name</label>
                <input
                  className="form-input"
                  autoFocus
                  placeholder="e.g. North America"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowCreate(false); }}
                />
              </div>
              <div className="form-field">
                <label className="form-label">Type</label>
                <div className="form-type-row">
                  <Chip isClickable isSelected={newType === 'OrganizationalUnit'} before={Icon.building} size="s" onClick={() => setNewType('OrganizationalUnit')}>
                    Org unit
                  </Chip>
                  <Chip isClickable isSelected={newType === 'Location'} before={Icon.location} size="s" onClick={() => setNewType('Location')}>
                    Location
                  </Chip>
                </div>
              </div>
              <div className="form-field">
                <label className="form-label">Parent</label>
                <ParentPicker value={newParent} nodes={nodes} onChange={setNewParent} />
              </div>
              <div className="form-actions">
                <Button emphasis="medium" size="s" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button emphasis="high" size="s" onClick={handleCreate} isDisabled={!newName.trim()}>Create</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OUPickerDropdown;
